"use client";

import { cn } from "@/lib/utils";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";

const SKELETON_REVEAL_DURATION_MS = 560;
const skeletonRevealStyle = {
  "--skeleton-reveal-duration": `${SKELETON_REVEAL_DURATION_MS}ms`,
} as CSSProperties;

interface SkeletonRevealProps {
  loading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
  className?: string;
  skeletonClassName?: string;
  contentClassName?: string;
}

/**
 * Keeps a loading skeleton mounted underneath the resolved UI, then reveals the
 * real component with a diagonal mask-style wipe. Best used when the skeleton
 * and loaded component share the same footprint.
 */
export function SkeletonReveal({
  loading,
  skeleton,
  children,
  className,
  skeletonClassName,
  contentClassName,
}: SkeletonRevealProps) {
  const [renderSkeleton, setRenderSkeleton] = useState(loading);

  useEffect(() => {
    const timeout = window.setTimeout(() => setRenderSkeleton(loading), loading ? 0 : SKELETON_REVEAL_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [loading]);

  return (
    <div className={cn("grid w-full", className)} style={skeletonRevealStyle}>
      {renderSkeleton && (
        <div
          className={cn(
            "skeleton-reveal-skeleton col-start-1 row-start-1",
            !loading && "skeleton-reveal-skeleton-exit",
            skeletonClassName,
          )}
          aria-hidden={!loading}
        >
          {skeleton}
        </div>
      )}

      {!loading && (
        <div className={cn("skeleton-reveal-content relative col-start-1 row-start-1", contentClassName)}>
          {children}
        </div>
      )}
    </div>
  );
}
